// Slice 2B1 · Trade Setup — progressive contractor-facing panel.
// Renders the trade queue in saved order. Roofing is fully editable
// (system picker + inline installed-selling form + Approve). Non-Roofing
// trades are read-only "coming next expansion" cards with a "Set up later"
// action. No dead-end unfinished forms.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, Sparkles, AlertTriangle } from "lucide-react";
import CohortTradePanel from "./CohortTradePanel";
import { useLang } from "@/context/LangContext";
// PO 2026-02 · Shared friendly required-field mapping.
import { buildMissingSummary } from "@/lib/tradeSetupFieldLabels";

// Phase 1 Trade Expansion — trades that render the new lightweight
// cohort panel below (non-Roofing). Roofing keeps its full pilot UI.
const COHORT_TRADES = new Set([
  "Flooring", "Painting", "Drywall", "Siding", "Insulation",
]);

const ROOFING_PILOT = [
  // Slice 2B1 Phase 1 canonical roofing systems — mirrored in
  // backend/roofing_taxonomy.ROOFING_TAXONOMY. Any drift here should
  // be resolved by regenerating this list from the backend.
  // Steep-Slope
  "Architectural Asphalt Shingles",
  // Metal
  "Standing-Seam Metal Roofing",
  "Exposed-Fastener / R-Panel Metal Roofing",
  // Tile
  "Concrete Tile Roofing",
  "Clay Tile Roofing",
  "Tile Lift and Relay",
  // Low-Slope
  "TPO — Mechanically Fastened over Fanfold / Recovery Board",
  "TPO — Mechanically Fastened with Flat Polyiso",
  "TPO — Mechanically Fastened with Tapered Polyiso",
  "TPO — Fully Adhered",
  "TPO — Recovery / Overlay",
  "TPO — Tear-Off and Replacement",
  "Tar & Gravel → TPO Replacement",
  "Modified Bitumen",
  "Spray Polyurethane Foam (SPF) Roofing",
];

// Slice 2B1 (Phase 1) — Roofing systems group into five NORMAL sections:
// Steep-Slope · Metal · Tile · Low-Slope · Custom. Legacy is NOT counted
// as a normal section — legacy rules surface via LegacyRoofingSystems.
const ROOFING_SECTIONS = [
  {
    key: "steep_slope",
    label: "Steep-Slope",
    systems: [
      "Architectural Asphalt Shingles",
    ],
  },
  {
    key: "metal",
    label: "Metal",
    systems: [
      "Standing-Seam Metal Roofing",
      "Exposed-Fastener / R-Panel Metal Roofing",
    ],
  },
  {
    key: "tile",
    label: "Tile",
    systems: [
      "Concrete Tile Roofing",
      "Clay Tile Roofing",
      "Tile Lift and Relay",
    ],
  },
  {
    key: "low_slope",
    label: "Low-Slope",
    systems: [
      "TPO — Mechanically Fastened over Fanfold / Recovery Board",
      "TPO — Mechanically Fastened with Flat Polyiso",
      "TPO — Mechanically Fastened with Tapered Polyiso",
      "TPO — Fully Adhered",
      "TPO — Recovery / Overlay",
      "TPO — Tear-Off and Replacement",
      "Tar & Gravel → TPO Replacement",
      "Modified Bitumen",
      "Spray Polyurethane Foam (SPF) Roofing",
    ],
  },
  { key: "custom", label: "Custom", systems: [] },
];

// Complete-installed helper copy shared across all installed_selling
// surfaces: Trade Setup, Pricing Settings, Approved-pricing picker,
// Auto-match review, Estimate line review, Simple Builder fallback.
const COMPLETE_INSTALLED_COPY = (
  "Complete installed price. This is the final customer price and " +
  "already includes materials, labor, overhead, and profit. JobClose AI " +
  "will not add another markup."
);

const PRICING_METHODS = [
  ["", "Choose how you normally price this work"],
  ["materials_labor", "Materials + Labor"],
  ["per_unit", "Price Per Unit"],
  ["flat", "Flat Price"],
  ["time_materials", "Time & Materials"],
  ["sub_markup", "Sub Quote + Markup"],
  ["combination", "Combination"],
  ["help_me", "Help Me"],
  ["later", "Set later (postpone this system)"],
];

const UNITS = [
  ["", "Choose unit"],
  ["sqft", "Square foot"],
  ["square", "Roofing square"],
  ["lf", "Linear foot"],
  ["item", "Item"],
];

const STATUS_LABEL = {
  ready: "Ready",
  ready_with_per_job_pricing: "Ready · Per-job pricing",
  deferred: "Set up later",
  in_progress: "Setup in progress",
  later: "Set up later",
};

const TradeSetupPanel = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const requestedTrade = params.get("trade") || "";
  const [queue, setQueue] = useState(null);
  // Slice 2B1 · Honor `?trade=` from the Step 1 "Continue setup" /
  // "Resume setup" / "Edit setup" contextual actions. Falls back to
  // Roofing so the pilot trade stays open by default when no trade
  // is requested (existing behavior).
  const [openTrade, setOpenTrade] = useState(requestedTrade || "Roofing");
  const [roofingData, setRoofingData] = useState(null);
  const [openSystem, setOpenSystem] = useState(null);

  // React to `?trade=` changes coming from same-page navigation (e.g.
  // clicking a different trade's contextual action from the Step 1
  // banner without a full page reload).
  useEffect(() => {
    if (requestedTrade) setOpenTrade(requestedTrade);
  }, [requestedTrade]);

  const refresh = async () => {
    const [q, r] = await Promise.all([
      api.get("/onboarding/trade-setup/queue"),
      api.get("/onboarding/trade-setup/trade/Roofing"),
    ]);
    setQueue(q.data);
    setRoofingData(r.data);
  };

  useEffect(() => { refresh(); }, []);

  if (!queue) return <div className="jc-card p-4 text-sm">Loading queue…</div>;

  return (
    <div className="space-y-3" data-testid="trade-setup-panel">
      {queue.items.map((it) => (
        <div key={it.trade}
             className="jc-card p-4"
             data-testid={`trade-row-${it.trade}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold" data-testid={`trade-row-${it.trade}-label`}>
                {it.trade}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)]"
                   data-testid={`trade-row-${it.trade}-status`}>
                {STATUS_LABEL[it.effective_status] || "Setup in progress"}
                {it.coming_soon && " · Coming in a future expansion"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!it.is_pilot && it.effective_status !== "later" && (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/onboarding/trade-setup/trade/${encodeURIComponent(it.trade)}/defer`);
                      toast.success(`${it.trade} marked as Set up later`);
                      refresh();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Set up later failed");
                    }
                  }}
                  className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                  data-testid={`trade-row-${it.trade}-later`}>
                  Set up later
                </button>
              )}
              {!it.is_pilot && it.effective_status === "later" && (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/onboarding/trade-setup/trade/${encodeURIComponent(it.trade)}/reopen`);
                      toast.success(`${it.trade} reopened`);
                      refresh();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Reopen failed");
                    }
                  }}
                  className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                  data-testid={`trade-row-${it.trade}-reopen`}>
                  Reopen
                </button>
              )}
              {it.is_pilot && (
                <button onClick={() => setOpenTrade(openTrade === it.trade ? null : it.trade)}
                        className="text-xs px-2 py-1 rounded jc-btn-primary"
                        data-testid={`trade-row-${it.trade}-open`}>
                  {openTrade === it.trade ? "Close" : "Configure"}
                </button>
              )}
            </div>
          </div>
          {it.trade === "Roofing" && openTrade === it.trade && (
            <div className="mt-3 border-t border-[var(--jc-border)] pt-3 space-y-2">
              {/* PO 2026-02 · Pricing completeness clarity — appears
                  above the canonical Roofing system list so a
                  contractor never mistakes unused systems for required
                  setup. Copy-only, no schema, no logic changes. */}
              <div
                data-testid="trade-setup-systems-optional-helper"
                className="text-xs text-[var(--jc-text-soft)] border-l-4 border-l-sky-400 bg-sky-50/50 px-3 py-2 rounded"
              >
                {t("soyb.systems_optional_helper")}
              </div>
              {ROOFING_SECTIONS.map((section) => (
                <div key={section.key}
                     className="jc-card p-3 bg-[var(--jc-surface)]/40"
                     data-testid={`roofing-section-${section.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]">
                      {section.label}
                    </div>
                    {section.comingSoon && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                        Coming next
                      </span>
                    )}
                  </div>
                  {section.systems.length === 0 && !section.comingSoon && (
                    <div className="text-xs text-[var(--jc-text-soft)] mt-2">
                      No systems yet. Add a custom system below.
                    </div>
                  )}
                  {section.systems.length === 0 && section.comingSoon && (
                    <div className="text-xs text-[var(--jc-text-soft)] mt-2">
                      Metal + Tile assembly-specific starters are landing in the next slice.
                    </div>
                  )}
                  {section.systems.map((label) => {
                    const sys = roofingData?.systems?.find(s => s.system_type === label);
                    const readiness = sys?.readiness?.kind || "not_started";
                    const approvedRule = sys?.approved_pricing_rule;
                    const derived = !!approvedRule && !!sys?.meta?.derived_from_pricing_rule_id;
                    return (
                      <div key={label}
                           className="mt-2 jc-card p-3 border-l-4"
                           style={{ borderLeftColor: readiness === "ready" ? "#16a34a" : readiness === "ready_with_per_job_pricing" ? "#0ea5e9" : readiness === "deferred" ? "#64748b" : "var(--jc-border-strong)" }}
                           data-testid={`roofing-system-${label}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">{label}</div>
                            <div className="text-xs text-[var(--jc-text-soft)]">
                              {readiness === "ready" && (<><CheckCircle2 size={12} className="inline mr-1" />Ready</>)}
                              {readiness === "ready_with_per_job_pricing" && "Ready · Per-job pricing"}
                              {readiness === "deferred" && "Set up later (deferred)"}
                              {readiness === "in_progress" && "Setup in progress"}
                              {readiness === "not_started" && "Not started"}
                              {approvedRule && (
                                <span data-testid={`roofing-system-${label}-approved-badge`}
                                      className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  Approved rate ${Number(approvedRule.selling_price_per_unit).toFixed(2)}/{approvedRule.selling_price_unit}
                                </span>
                              )}
                            </div>
                          </div>
                          {approvedRule ? (
                            <a
                              href="/app/pricing-profile#approved-trade-pricing-section"
                              data-testid={`roofing-system-${label}-edit-setup`}
                              data-approved-rule-id={approvedRule.pricing_rule_id}
                              className="text-xs px-2 py-1 rounded jc-btn-primary"
                            >
                              Edit setup
                            </a>
                          ) : (
                            <button onClick={() => setOpenSystem(openSystem === label ? null : label)}
                                    className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                                    data-testid={`roofing-system-${label}-toggle`}>
                              {openSystem === label ? "Close" : "Configure"} <ChevronRight size={10} className="inline" />
                            </button>
                          )}
                        </div>
                        {approvedRule && (
                          <div className="mt-2 rounded bg-emerald-50 border border-emerald-200 p-2 text-[11px] text-emerald-900"
                               data-testid={`roofing-system-${label}-approved-source`}>
                            Pricing source: your Pricing Settings · Complete installed price ({approvedRule.pricing_type === "installed_selling" ? "installed_selling" : approvedRule.pricing_type}). This approved rate will be used for future matching estimates. Existing estimates remain unchanged snapshots. JobClose AI will not add another markup.
                          </div>
                        )}
                        {!approvedRule && openSystem === label && (
                          <>
                            <SystemForm label={label} meta={sys?.meta || {}} onSaved={refresh} />
                            <ReferenceDocLibrary trade="Roofing" system={label} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              {/* Slice 2B1 (assemblies) — legacy broad TPO rules that a
                  tenant already has on file. Rendered as a distinct
                  card with a classify-into-assembly prompt. The rule
                  is NEVER silently renamed. */}
              <LegacyRoofingSystems roofingData={roofingData} onSaved={refresh} />
              {/* Slice 2B1 · Custom / One-Off Roofing systems + historical
                  pricing rows review. */}
              <CustomSystemsSection roofingData={roofingData} onSaved={refresh} />
              <HistoricalPricingRows roofingData={roofingData} onSaved={refresh} />
            </div>
          )}
          {COHORT_TRADES.has(it.trade) && openTrade === it.trade && (
            <CohortTradePanel trade={it.trade} />
          )}
        </div>
      ))}
      <div className="jc-card p-3 text-xs text-[var(--jc-text-soft)]"
           data-testid="trade-setup-help">
        <Sparkles size={12} className="inline mr-1" />
        Trade Setup is a one-time guided setup. The contractor chooses how they normally price their work, enters or confirms their own pricing, reviews what is normally included, and approves it. Set up one Roofing system as Ready to complete Step 2. Other trades can be Set up later.
      </div>
    </div>
  );
};

const SystemForm = ({ label, meta, onSaved }) => {
  const { t, lang } = useLang();
  const [f, setF] = useState({
    selected: true,
    pricing_method: meta.pricing_method || "",
    pricing_structure: meta.pricing_structure || "",
    selling_price_per_unit: meta.selling_price_per_unit ?? "",
    selling_price_unit: meta.selling_price_unit || "",
    flat_price: meta.flat_price ?? "",
    labor_amount: meta.labor_amount ?? "",
    labor_notes: meta.labor_notes || "",
    material_source: meta.material_source || "",
    material_source_notes: meta.material_source_notes || "",
    labor_approach: meta.labor_approach || "",
    markup_handling: meta.markup_handling || "",
    markup_pct: meta.markup_pct ?? "",
    margin_pct: meta.margin_pct ?? "",
    scope_defaults: meta.scope_defaults || { scope: "" },
    zero_selling_price_confirmed: meta.zero_selling_price_confirmed || false,
    profit_included_confirmed: meta.profit_included_confirmed || false,
    materials_supplied_by: meta.materials_supplied_by || "",
  });
  const [saving, setSaving] = useState(false);
  // PO 2026-02 · Trade Setup required-field UX. When the backend
  // returns system_setup_incomplete we surface a friendly summary
  // (never raw snake_case) via the shared helper.
  const [missingFields, setMissingFields] = useState([]);

  const method = f.pricing_method;
  const isInstalledSelling = method === "per_unit"
    && f.pricing_structure === "complete_installed";
  const isFlat = method === "flat";
  const isTM = method === "time_materials";
  const isSubMarkup = method === "sub_markup";
  const isMaterialsLabor = method === "materials_labor";
  const isCombination = method === "combination";
  const isHelpMe = method === "help_me";
  const isSetLater = method === "later";

  const save = async (approve = false) => {
    setSaving(true);
    setMissingFields([]);
    try {
      const body = { ...f };
      // Coerce numeric fields.
      const numFields = ["selling_price_per_unit", "flat_price", "labor_amount", "markup_pct", "margin_pct"];
      numFields.forEach(k => {
        if (body[k] === "" || body[k] === undefined) body[k] = null;
        else body[k] = parseFloat(body[k]);
      });
      // For installed-selling systems, markup_handling MUST be built_in.
      if (isInstalledSelling && !body.markup_handling) body.markup_handling = "built_in";
      const url = `/onboarding/trade-setup/trade/Roofing/system/${encodeURIComponent(label)}`;
      await api.put(url, body);
      if (approve) {
        // Zero-price systems must have server-side confirmation timestamp.
        const zeroPrice = (isInstalledSelling && body.selling_price_per_unit === 0 && f.zero_selling_price_confirmed)
          || (isFlat && body.flat_price === 0 && f.zero_selling_price_confirmed);
        if (zeroPrice) {
          await api.post(`${url}/zero-confirm`, {});
        }
        await api.post(`${url}/approve`, {});
        toast.success(`${label} is Ready`);
      } else {
        toast.success("Saved");
      }
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "system_setup_incomplete") {
        const miss = Array.isArray(detail.missing) ? detail.missing : [];
        setMissingFields(miss);
        setTimeout(() => {
          const first = miss[0];
          if (!first) return;
          const el = document.querySelector(
            `[data-testid="system-form-${label}-${first.replace(/_/g,'-')}"]`
          );
          if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 60);
        toast.error(buildMissingSummary(miss, lang));
      } else {
        toast.error("Save failed — please retry");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 text-sm" data-testid={`system-form-${label}`}>
      {/* PO 2026-02 · Friendly required-field summary. Only renders
          after the backend flags missing fields — no raw snake_case
          tokens are ever shown to the contractor. */}
      {missingFields.length > 0 && (
        <div
          data-testid={`system-form-${label}-missing-summary`}
          className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>{buildMissingSummary(missingFields, lang)}</div>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold">How do you normally price this work?</label>
        <select value={f.pricing_method}
                onChange={e => setF({ ...f, pricing_method: e.target.value })}
                data-testid={`system-form-${label}-pricing-method`}
                className="w-full mt-1 border rounded p-2">
          {PRICING_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {isHelpMe && (
        <div className="rounded bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
             data-testid={`system-form-${label}-help-me-panel`}>
          Not sure how to price this system? Save your choice — a JobClose AI
          specialist will follow up during onboarding. This system stays in
          "Setup in progress" until you choose a specific method or a
          specialist helps you configure it.
        </div>
      )}
      {isSetLater && (
        <div className="rounded bg-slate-50 border border-slate-200 p-3 text-xs text-slate-800"
             data-testid={`system-form-${label}-set-later-panel`}>
          <strong>Set later</strong> — you're postponing setup for this system. No pricing rule will be created, and this system won't appear in the estimate picker. You can return here at any time to complete setup. This is different from <em>per-job pricing</em>, which means you intentionally price each job in the field.
        </div>
      )}
      {method === "per_unit" && (
        <div>
          <label className="text-xs font-semibold">Pricing structure</label>
          <select value={f.pricing_structure}
                  onChange={e => setF({ ...f, pricing_structure: e.target.value })}
                  data-testid={`system-form-${label}-structure`}
                  className="w-full mt-1 border rounded p-2">
            <option value="">Choose structure</option>
            <option value="complete_installed">Complete installed selling price</option>
            <option value="separate_ml">Materials + Labor entered separately</option>
            <option value="per_job">Priced per job</option>
            <option value="later">Set later</option>
          </select>
        </div>
      )}
      {isInstalledSelling && (
        <>
          <div className="rounded bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 space-y-2"
               data-testid={`system-form-${label}-installed-copy`}>
            <div className="font-semibold text-blue-900">
              Complete installed price
            </div>
            <div>
              This is the final customer price you normally charge for this work and already includes everything below. JobClose AI will not add another markup.
            </div>
            <ul className="list-disc list-inside space-y-0.5"
                data-testid={`system-form-${label}-installed-copy-bullets`}>
              <li>Materials included</li>
              <li>Labor included</li>
              <li>Overhead included</li>
              <li>Profit included</li>
              <li>No additional markup</li>
            </ul>
            <div className="text-[11px] text-blue-800">
              This approved rate will be used for future matching estimates and is saved to your approved Pricing Settings. Existing estimates remain unchanged snapshots. Tax remains separate where configured.
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" className="mt-0.5"
                   checked={f.profit_included_confirmed}
                   onChange={e => setF({ ...f, profit_included_confirmed: e.target.checked })}
                   data-testid={`system-form-${label}-profit-confirm`} />
            <span>My profit is already included in this price.</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">Selling price</label>
              <input type="number" step="0.01" min="0"
                     value={f.selling_price_per_unit}
                     onChange={e => setF({ ...f, selling_price_per_unit: e.target.value })}
                     placeholder="Enter your price"
                     data-testid={`system-form-${label}-price`}
                     className="w-full mt-1 border rounded p-2" />
            </div>
            <div>
              <label className="text-xs font-semibold">Unit</label>
              <select value={f.selling_price_unit}
                      onChange={e => setF({ ...f, selling_price_unit: e.target.value })}
                      data-testid={`system-form-${label}-unit`}
                      className="w-full mt-1 border rounded p-2">
                {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          {parseFloat(f.selling_price_per_unit || "0") === 0 && f.selling_price_per_unit !== "" && (
            <label className="flex items-start gap-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
              <input type="checkbox" className="mt-0.5"
                     checked={f.zero_selling_price_confirmed}
                     onChange={e => setF({ ...f, zero_selling_price_confirmed: e.target.checked })}
                     data-testid={`system-form-${label}-zero-confirm`} />
              <span>I intentionally want to bill $0 for this system.</span>
            </label>
          )}
        </>
      )}
      {isFlat && (
        <>
          <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs text-blue-900">
            Flat Price is the single all-in price you normally charge for this system on a typical job. Enter the amount you would quote a customer — JobClose AI will not add another markup on top.
          </div>
          <div>
            <label className="text-xs font-semibold">Flat price</label>
            <input type="number" step="0.01" min="0"
                   value={f.flat_price}
                   onChange={e => setF({ ...f, flat_price: e.target.value })}
                   placeholder="Enter the typical total"
                   data-testid={`system-form-${label}-flat-price`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
          {parseFloat(f.flat_price || "0") === 0 && f.flat_price !== "" && (
            <label className="flex items-start gap-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
              <input type="checkbox" className="mt-0.5"
                     checked={f.zero_selling_price_confirmed}
                     onChange={e => setF({ ...f, zero_selling_price_confirmed: e.target.checked })}
                     data-testid={`system-form-${label}-flat-zero-confirm`} />
              <span>I intentionally want to bill $0 for this system.</span>
            </label>
          )}
        </>
      )}
      {isTM && (
        <div className="rounded bg-orange-50 border border-orange-200 p-2 text-xs text-orange-900">
          Time &amp; Materials — the final total is calculated per job from actual hours plus materials. Enter your typical rate below.
        </div>
      )}
      {isSubMarkup && (
        <div className="rounded bg-orange-50 border border-orange-200 p-2 text-xs text-orange-900">
          Sub Quote + Markup — the total comes from the subcontractor's quote for the job, with your markup applied on top.
        </div>
      )}
      {isCombination && (
        <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs text-blue-900">
          Combination — you use a mix of methods per job. Describe how you break it up so we can help you price consistently.
        </div>
      )}
      {!isHelpMe && !isSetLater && (
      <div>
        <label className="text-xs font-semibold">Who supplies the materials?</label>
        <select value={f.materials_supplied_by || ""}
                onChange={e => setF({ ...f, materials_supplied_by: e.target.value })}
                data-testid={`system-form-${label}-materials-supplied`}
                className="w-full mt-1 border rounded p-2">
          <option value="">Choose (or Reviewed per job)</option>
          <option value="contractor">Contractor</option>
          <option value="customer">Customer</option>
          <option value="builder">Builder</option>
          <option value="supplier_direct">Supplier-direct</option>
          <option value="per_job">Reviewed per job</option>
        </select>
      </div>
      )}
      {!isHelpMe && !isSetLater && (
      <div>
        <label className="text-xs font-semibold">Material approach</label>
        <select value={f.material_source}
                onChange={e => setF({ ...f, material_source: e.target.value })}
                data-testid={`system-form-${label}-material-source`}
                className="w-full mt-1 border rounded p-2">
          <option value="">Choose</option>
          <option value="supplier_current">Current supplier pricing</option>
          <option value="invoice">Recent supplier invoice</option>
          <option value="workbook">JobClose AI trade/system workbook</option>
          <option value="spreadsheet">Contractor spreadsheet</option>
          <option value="manual">Manual entry</option>
          <option value="retail">Local retail fallback</option>
          <option value="per_job">Per-job pricing</option>
          <option value="later">Set later</option>
        </select>
      </div>
      )}
      {!isHelpMe && !isSetLater && f.material_source === "workbook" && (
        <WorkbookControls systemType={label} />
      )}
      {!isHelpMe && !isSetLater && (
      <div>
        <label className="text-xs font-semibold">Labor approach</label>
        <select value={f.labor_approach}
                onChange={e => setF({ ...f, labor_approach: e.target.value })}
                data-testid={`system-form-${label}-labor-approach`}
                className="w-full mt-1 border rounded p-2">
          <option value="">Choose</option>
          <option value="included_in_installed">Complete labor included in installed price</option>
          <option value="per_square">Complete labor per square</option>
          <option value="per_sqft">Complete labor per square foot</option>
          <option value="per_lf">Complete labor per linear foot</option>
          <option value="per_item">Complete labor per item</option>
          <option value="flat">Flat labor price</option>
          <option value="hourly">Hourly</option>
          <option value="daily">Daily</option>
          <option value="sub_quote">Subcontractor quote</option>
          <option value="per_job">Per-job labor</option>
          <option value="later">Set later</option>
        </select>
      </div>
      )}
      {!isHelpMe && !isSetLater && !isInstalledSelling && (f.labor_approach === "hourly" || f.labor_approach === "daily" || f.labor_approach === "flat" || f.labor_approach === "per_square" || f.labor_approach === "per_sqft" || f.labor_approach === "per_lf" || f.labor_approach === "per_item") && (
        <div>
          <label className="text-xs font-semibold">
            Labor rate / amount ({f.labor_approach === "hourly" ? "$/hr" : f.labor_approach === "daily" ? "$/day" : f.labor_approach === "flat" ? "$ total" : "$/unit"})
          </label>
          <input type="number" step="0.01" min="0"
                 value={f.labor_amount}
                 onChange={e => setF({ ...f, labor_amount: e.target.value })}
                 placeholder="Enter your rate"
                 data-testid={`system-form-${label}-labor-amount`}
                 className="w-full mt-1 border rounded p-2" />
        </div>
      )}
      {isCombination && (
        <div>
          <label className="text-xs font-semibold">Combination notes (required)</label>
          <textarea value={f.material_source_notes || ""}
                    onChange={e => setF({ ...f, material_source_notes: e.target.value })}
                    placeholder="Describe how you combine methods on a typical job"
                    data-testid={`system-form-${label}-combo-notes`}
                    className="w-full mt-1 border rounded p-2" rows={2} />
        </div>
      )}
      {!isHelpMe && !isSetLater && (
      <div>
        <label className="text-xs font-semibold">Scope summary (typical work)</label>
        <textarea value={f.scope_defaults?.scope || ""}
                  onChange={e => setF({ ...f, scope_defaults: { ...(f.scope_defaults || {}), scope: e.target.value } })}
                  data-testid={`system-form-${label}-scope`}
                  className="w-full mt-1 border rounded p-2" rows={3} />
      </div>
      )}
      {!isHelpMe && !isSetLater && (
      <div>
        <label className="text-xs font-semibold">Markup / margin / profit handling</label>
        <select value={f.markup_handling}
                onChange={e => setF({ ...f, markup_handling: e.target.value })}
                data-testid={`system-form-${label}-markup`}
                className="w-full mt-1 border rounded p-2">
          <option value="">Choose</option>
          <option value="same_ml_markup">Same markup on materials and labor</option>
          <option value="different_ml_markups">Different markups for materials and labor</option>
          <option value="markup_included">Markup already included</option>
          <option value="separate_overhead_profit">Separate overhead and profit</option>
          <option value="target_gross_margin">Target gross margin</option>
          <option value="fixed_overhead_profit">Fixed overhead and profit</option>
          <option value="built_in">Built into selling price</option>
          <option value="per_job">Per-job review</option>
          <option value="another_method">Another method</option>
          <option value="help_me_calculate">Help me calculate</option>
          <option value="later">Set later</option>
        </select>
      </div>
      )}
      {!isHelpMe && !isSetLater && (f.markup_handling === "same_ml_markup" || f.markup_handling === "markup") && (
        <div>
          <label className="text-xs font-semibold">Markup %</label>
          <input type="number" step="0.1" min="0" value={f.markup_pct}
                 onChange={e => setF({ ...f, markup_pct: e.target.value })}
                 placeholder="Enter your markup"
                 data-testid={`system-form-${label}-markup-pct`}
                 className="w-full mt-1 border rounded p-2" />
        </div>
      )}
      {!isHelpMe && !isSetLater && f.markup_handling === "different_ml_markups" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-semibold">Material markup %</label>
            <input type="number" step="0.1" min="0" value={f.markup_pct}
                   onChange={e => setF({ ...f, markup_pct: e.target.value })}
                   data-testid={`system-form-${label}-material-markup-pct`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
          <div>
            <label className="text-xs font-semibold">Labor markup %</label>
            <input type="number" step="0.1" min="0" value={f.labor_markup_pct || ""}
                   onChange={e => setF({ ...f, labor_markup_pct: e.target.value })}
                   data-testid={`system-form-${label}-labor-markup-pct`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
        </div>
      )}
      {!isHelpMe && !isSetLater && (f.markup_handling === "target_gross_margin" || f.markup_handling === "margin") && (
        <div>
          <label className="text-xs font-semibold">Target gross margin %</label>
          <input type="number" step="0.1" min="0" value={f.margin_pct}
                 onChange={e => setF({ ...f, margin_pct: e.target.value })}
                 placeholder="Enter your margin"
                 data-testid={`system-form-${label}-margin-pct`}
                 className="w-full mt-1 border rounded p-2" />
        </div>
      )}
      {!isHelpMe && !isSetLater && f.markup_handling === "separate_overhead_profit" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-semibold">Overhead %</label>
            <input type="number" step="0.1" min="0" value={f.overhead_pct || ""}
                   onChange={e => setF({ ...f, overhead_pct: e.target.value })}
                   data-testid={`system-form-${label}-overhead-pct`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
          <div>
            <label className="text-xs font-semibold">Profit %</label>
            <input type="number" step="0.1" min="0" value={f.profit_pct || ""}
                   onChange={e => setF({ ...f, profit_pct: e.target.value })}
                   data-testid={`system-form-${label}-profit-pct`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
        </div>
      )}
      {!isHelpMe && !isSetLater && f.markup_handling === "fixed_overhead_profit" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-semibold">Overhead $</label>
            <input type="number" step="0.01" min="0" value={f.overhead_amount || ""}
                   onChange={e => setF({ ...f, overhead_amount: e.target.value })}
                   data-testid={`system-form-${label}-overhead-amount`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
          <div>
            <label className="text-xs font-semibold">Profit $</label>
            <input type="number" step="0.01" min="0" value={f.profit_amount || ""}
                   onChange={e => setF({ ...f, profit_amount: e.target.value })}
                   data-testid={`system-form-${label}-profit-amount`}
                   className="w-full mt-1 border rounded p-2" />
          </div>
        </div>
      )}
      {!isHelpMe && !isSetLater && f.markup_handling === "another_method" && (
        <div>
          <label className="text-xs font-semibold">Describe your method (required)</label>
          <textarea value={f.markup_notes || ""}
                    onChange={e => setF({ ...f, markup_notes: e.target.value })}
                    placeholder="Describe how you handle markup / margin / profit"
                    data-testid={`system-form-${label}-markup-notes`}
                    className="w-full mt-1 border rounded p-2" rows={2} />
        </div>
      )}
      {!isHelpMe && !isSetLater && f.markup_handling === "help_me_calculate" && (
        <div className="rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900"
             data-testid={`system-form-${label}-help-me-calc-panel`}>
          A JobClose AI specialist will help you calculate your markup / margin during onboarding. This system stays in "Setup in progress" until you finalize a number.
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <button onClick={() => save(false)} disabled={saving}
                className="text-xs px-3 py-2 rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                data-testid={`system-form-${label}-save`}>
          Save
        </button>
        <button onClick={() => save(true)} disabled={saving || (isInstalledSelling && !f.profit_included_confirmed) || isHelpMe}
                className="text-xs px-3 py-2 rounded jc-btn-primary"
                data-testid={`system-form-${label}-approve`}>
          {isSetLater ? "Save as Set later" : "Approve as Ready"}
        </button>
      </div>
    </div>
  );
};

export default TradeSetupPanel;

// ============================================================================
// Slice 2B1 · JobClose AI Pricing Workbook (download / import → DRAFT review)
// ============================================================================
const WorkbookControls = ({ systemType, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [uploadInfo, setUploadInfo] = useState(null);

  const loadDrafts = async () => {
    try {
      const { data } = await api.get(`/onboarding/trade-setup/workbook/${encodeURIComponent(systemType)}/drafts`);
      setDrafts(data.drafts || []);
    } catch { setDrafts([]); }
  };
  useEffect(() => { loadDrafts(); }, [systemType]);

  const download = async () => {
    setBusy(true);
    try {
      const url = `${axiosBase()}/api/onboarding/trade-setup/workbook/${encodeURIComponent(systemType)}/template.xlsx`;
      const res = await fetch(url, { headers: authHeader() });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `jobclose-workbook-${systemType.slice(0,40)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Workbook download failed"); }
    finally { setBusy(false); }
  };

  const onFile = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const url = `${axiosBase()}/api/onboarding/trade-setup/workbook/${encodeURIComponent(systemType)}/import`;
      const res = await fetch(url, { method: "POST", headers: authHeader(), body: form });
      const body = await res.json();
      if (!res.ok) {
        const detail = body?.detail;
        if (detail?.code === "workbook_row_errors") toast.error("Workbook rows contain errors — check headers.");
        else if (detail?.code === "workbook_version_mismatch") toast.error("Workbook version mismatch — please download a fresh template.");
        else if (detail?.code === "workbook_system_mismatch") toast.error("Workbook system mismatch.");
        else if (detail?.code === "workbook_missing_sheets") toast.error("Workbook must include Materials + Labor sheets.");
        else toast.error(detail?.code || detail || "Import failed");
      } else {
        setUploadInfo(body);
        toast.success(`${body.draft_rows_created} draft rows ready for review — nothing is Ready yet.`);
        await loadDrafts();
      }
    } catch { toast.error("Workbook import failed"); }
    finally { setBusy(false); evt.target.value = ""; }
  };

  const setStatus = async (draft_id, status) => {
    try {
      await api.patch(`/onboarding/trade-setup/workbook/drafts/${draft_id}`, { status });
      await loadDrafts();
    } catch { toast.error("Update failed"); }
  };

  const approveAccepted = async () => {
    try {
      const { data } = await api.post(`/onboarding/trade-setup/workbook/${encodeURIComponent(systemType)}/drafts/approve`);
      toast.success(`${data.approved_rows} rows approved as reusable pricing`);
      await loadDrafts();
      onSaved && onSaved();
    } catch { toast.error("Approve failed"); }
  };

  return (
    <div className="rounded border border-dashed border-[var(--jc-border-strong)] p-2 space-y-2"
         data-testid={`workbook-${systemType}`}>
      <div className="text-xs font-semibold">JobClose AI Pricing Workbook</div>
      <div className="text-[11px] text-[var(--jc-text-soft)]">
        Download a system-specific .xlsx template with <strong>Materials</strong> and <strong>Labor</strong> sheets. Upload does NOT create reusable pricing — imported rows land in a draft review below.
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={download} disabled={busy}
                className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                data-testid={`workbook-${systemType}-download`}>Download template</button>
        <label className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)] cursor-pointer"
               data-testid={`workbook-${systemType}-import-label`}>
          Import filled workbook
          <input type="file" accept=".xlsx" disabled={busy} onChange={onFile}
                 className="hidden"
                 data-testid={`workbook-${systemType}-import`} />
        </label>
      </div>
      {uploadInfo && (
        <div className="text-[11px] text-[var(--jc-text-soft)]"
             data-testid={`workbook-${systemType}-upload-info`}>
          {uploadInfo.source_filename} · {uploadInfo.draft_rows_created} draft rows staged
        </div>
      )}
      {drafts.length > 0 && (
        <div className="space-y-1" data-testid={`workbook-${systemType}-drafts`}>
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">Draft rows — review before approving</div>
            <button onClick={approveAccepted}
                    className="text-xs px-2 py-1 rounded jc-btn-primary"
                    data-testid={`workbook-${systemType}-approve-accepted`}>
              Approve accepted rows
            </button>
          </div>
          <ul className="space-y-1">
            {drafts.map(d => (
              <li key={d.draft_id}
                  className="rounded bg-[var(--jc-bg)] px-2 py-1 text-[11px] flex items-center justify-between gap-2"
                  data-testid={`workbook-${systemType}-draft-${d.draft_id}`}>
                <div className="truncate">
                  <span className="font-medium">{d.sheet}</span>
                  {" · "}
                  <span>{d.data?.material_or_service || d.data?.labor_or_assembly_description || "(unnamed)"}</span>
                  <span className="ml-2 text-[10px] text-[var(--jc-text-soft)]">
                    {d.source_filename} row {d.source_row} · status: {d.status}
                    {d.issues && d.issues.length > 0 && ` · issues: ${d.issues.join(", ")}`}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setStatus(d.draft_id, "accepted")}
                          disabled={d.status === "accepted"}
                          className="text-[10px] px-1 rounded border border-green-600 text-green-700"
                          data-testid={`workbook-${systemType}-accept-${d.draft_id}`}>Accept</button>
                  <button onClick={() => setStatus(d.draft_id, "deferred")}
                          className="text-[10px] px-1 rounded border border-slate-500 text-slate-700"
                          data-testid={`workbook-${systemType}-defer-${d.draft_id}`}>Defer</button>
                  <button onClick={() => setStatus(d.draft_id, "dismissed")}
                          className="text-[10px] px-1 rounded border border-red-500 text-red-700"
                          data-testid={`workbook-${systemType}-dismiss-${d.draft_id}`}>Dismiss</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Slice 2B1 · GridFS-backed reference-document library
// ============================================================================
const ALLOWED_REF_EXTS = ["pdf", "jpg", "jpeg", "png", "csv", "xls", "xlsx"];
const REF_MAX_MB = 10;
const ReferenceDocLibrary = ({ trade, system }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/onboarding/trade-setup/reference-doc/list`,
        { params: { trade, system_type: system } });
      setDocs(data.docs || []);
    } catch { setDocs([]); }
  };
  useEffect(() => { load(); }, [trade, system]);

  const upload = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_REF_EXTS.includes(ext)) {
      toast.error(`Unsupported file type — allowed: ${ALLOWED_REF_EXTS.join(", ").toUpperCase()}`);
      evt.target.value = ""; return;
    }
    if (file.size > REF_MAX_MB * 1024 * 1024) {
      toast.error(`File too large — max ${REF_MAX_MB} MB per file`);
      evt.target.value = ""; return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("trade", trade);
      form.append("system_type", system);
      form.append("file", file);
      const url = `${axiosBase()}/api/onboarding/trade-setup/reference-doc/upload`;
      const res = await fetch(url, { method: "POST", headers: authHeader(), body: form });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail?.code || "upload_failed");
      toast.success("Uploaded as an internal pricing reference.");
      await load();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setBusy(false);
      evt.target.value = "";
    }
  };

  const preview = (d) => {
    const url = `${axiosBase()}/api/onboarding/trade-setup/reference-doc/preview/${d.gridfs_id}`;
    const token = localStorage.getItem("jc_token") || localStorage.getItem("token");
    // Preview endpoint requires the Authorization header — fetch to blob
    // then open the blob URL in a new tab.
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (body?.detail?.code === "preview_unsupported") {
            downloadDoc(d);
            return;
          }
          throw new Error("preview_failed");
        }
        const blob = await res.blob();
        window.open(URL.createObjectURL(blob), "_blank");
      })
      .catch(() => toast.error("Preview unavailable — try Download."));
  };

  const downloadDoc = (d) => {
    const url = `${axiosBase()}/api/onboarding/trade-setup/reference-doc/download/${d.gridfs_id}`;
    const token = localStorage.getItem("jc_token") || localStorage.getItem("token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = d.filename || "reference";
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("Download failed"));
  };

  const detach = async (gid) => {
    try {
      await api.post(`/onboarding/trade-setup/reference-doc/${gid}/detach`,
        { trade, system_type: system });
      toast.success("Detached from this system");
      await load();
    } catch { toast.error("Detach failed"); }
  };

  const attachAnother = async (gid, targetSystem) => {
    if (!targetSystem) return;
    try {
      await api.post(`/onboarding/trade-setup/reference-doc/${gid}/attach`,
        { trade, system_type: targetSystem });
      toast.success(`Also attached to ${targetSystem}`);
      await load();
    } catch { toast.error("Attach failed"); }
  };

  const confirmAndDelete = async (gid) => {
    try {
      await api.delete(`/onboarding/trade-setup/reference-doc/${gid}?confirm=true`);
      toast.success("Reference document deleted");
      setConfirmDelete(null);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-3 rounded border border-dashed border-[var(--jc-border-strong)] p-2 space-y-1"
         data-testid={`refdocs-${system}`}>
      <div className="text-xs font-semibold">Reference documents</div>
      <div className="text-[11px] text-[var(--jc-text-soft)]">
        Uploaded as an internal pricing reference. Not sent to customers, not analyzed by AI. PDF · JPG · PNG · CSV · XLS · XLSX (max {REF_MAX_MB} MB each). Documents are contractor-wide — you can attach the same document to multiple systems.
      </div>
      <label className="inline-block text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)] cursor-pointer"
             data-testid={`refdocs-${system}-upload-label`}>
        Upload document
        <input type="file"
               accept=".pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
               disabled={busy}
               onChange={upload}
               className="hidden"
               data-testid={`refdocs-${system}-upload`} />
      </label>
      {docs.length > 0 && (
        <ul className="mt-1 space-y-1" data-testid={`refdocs-${system}-list`}>
          {docs.map(d => {
            const ct = (d.content_type || "").toLowerCase();
            const previewable = ct.startsWith("image/") || ct === "application/pdf";
            return (
              <li key={d.gridfs_id}
                  className="text-xs rounded bg-[var(--jc-bg)] px-2 py-1 space-y-1"
                  data-testid={`refdocs-${system}-item-${d.gridfs_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate">
                    <span className="font-medium">{d.filename}</span>
                    <span className="ml-2 text-[10px] text-[var(--jc-text-soft)]">
                      {Math.round((d.size_bytes || 0)/1024)} KB · attached to {(d.associations||[]).length} system{(d.associations||[]).length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {previewable ? (
                      <button onClick={() => preview(d)}
                              className="text-[10px] px-1 rounded border border-[var(--jc-border-strong)]"
                              data-testid={`refdocs-${system}-preview-${d.gridfs_id}`}>Preview</button>
                    ) : (
                      <button onClick={() => downloadDoc(d)}
                              className="text-[10px] px-1 rounded border border-[var(--jc-border-strong)]"
                              data-testid={`refdocs-${system}-download-${d.gridfs_id}`}>Download to open</button>
                    )}
                    <button onClick={() => detach(d.gridfs_id)}
                            className="text-[10px] px-1 rounded border border-amber-500 text-amber-700"
                            data-testid={`refdocs-${system}-detach-${d.gridfs_id}`}>Detach</button>
                    <button onClick={() => setConfirmDelete(d.gridfs_id)}
                            className="text-[10px] px-1 rounded border border-red-500 text-red-700"
                            data-testid={`refdocs-${system}-delete-${d.gridfs_id}`}>Delete…</button>
                  </div>
                </div>
                {confirmDelete === d.gridfs_id && (
                  <div className="rounded bg-red-50 border border-red-200 p-1 text-[11px] text-red-900"
                       data-testid={`refdocs-${system}-confirm-${d.gridfs_id}`}>
                    Delete <strong>{d.filename}</strong>? This removes the file and every association.
                    <div className="mt-1 flex gap-2">
                      <button onClick={() => confirmAndDelete(d.gridfs_id)}
                              className="text-[10px] px-1 rounded jc-btn-primary"
                              data-testid={`refdocs-${system}-confirm-yes-${d.gridfs_id}`}>Yes, delete</button>
                      <button onClick={() => setConfirmDelete(null)}
                              className="text-[10px] px-1 rounded border border-[var(--jc-border-strong)]"
                              data-testid={`refdocs-${system}-confirm-no-${d.gridfs_id}`}>Cancel</button>
                    </div>
                  </div>
                )}
                {(d.associations || []).length > 1 && (
                  <div className="text-[10px] text-[var(--jc-text-soft)]">
                    Also on: {(d.associations||[]).filter(a => a.system_type !== system).map(a => a.system_type).join(", ")}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ============================================================================
// Slice 2B1 (assemblies) · Legacy broad-TPO systems still on file
// ============================================================================
// Renders a distinct card for each existing tenant system stored under a
// legacy broad label (currently just ``TPO — Mechanically Fastened``).
// The rule / metadata is NEVER silently renamed. The contractor must
// explicitly choose an assembly-specific classification or "Keep as
// custom legacy system" before Trade Setup treats the rule as classified.
const LegacyRoofingSystems = ({ roofingData, onSaved }) => {
  const legacies = (roofingData?.systems || []).filter(
    (s) => s.legacy_needs_classification === true,
  );
  if (legacies.length === 0) return null;
  return (
    <div className="jc-card p-3 border-l-4 border-l-amber-500 bg-amber-50/40"
         data-testid="roofing-legacy-systems-section">
      <div className="text-xs font-semibold uppercase tracking-wider text-amber-900">
        Legacy systems needing classification
      </div>
      <div className="text-xs text-amber-800 mt-1">
        These systems were saved before assembly-specific TPO variants
        existed. Choose the roof assembly each represents. Your existing
        PricingRule IDs and historical estimates stay intact.
      </div>
      <div className="mt-3 space-y-3">
        {legacies.map((s) => (
          <LegacyClassifyCard key={s.system_type} system={s} onSaved={onSaved} />
        ))}
      </div>
    </div>
  );
};

const LegacyClassifyCard = ({ system, onSaved }) => {
  const [options, setOptions] = useState(null);
  const [ruleId, setRuleId] = useState(null);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(
          `/onboarding/trade-setup/legacy-classifications/Roofing/${encodeURIComponent(system.system_type)}`
        );
        setOptions(r.data.options || []);
      } catch (e) {
        setOptions([]);
      }
      // Resolve the tenant-owned PricingRule id for this legacy system_type.
      try {
        const p = await api.get("/onboarding/trade-setup/estimate-picker/Roofing");
        const match = (p.data.systems || []).find(
          (x) => x.system_type === system.system_type && x.needs_legacy_classification,
        );
        if (match) setRuleId(match.pricing_rule_id);
      } catch (e) { /* no-op — rule may not be approved yet */ }
    })();
  }, [system.system_type]);

  const classify = async () => {
    if (!ruleId || !choice) return;
    setSaving(true);
    try {
      await api.post(`/onboarding/trade-setup/pricing-rule/${ruleId}/classify-legacy`, {
        classification: choice,
      });
      toast.success(
        choice === "keep_legacy"
          ? "Kept as a custom legacy system."
          : "Classified — the rule now uses the assembly-specific name.",
      );
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Classification failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="jc-card p-3 bg-white"
         data-testid={`roofing-legacy-card-${system.system_type}`}>
      <div className="text-sm font-semibold">{system.system_type}</div>
      <div className="text-[11px] text-[var(--jc-text-soft)] mt-1">
        This system was previously saved as <b>{system.system_type}</b>. Choose the roof assembly it represents.
      </div>
      {!ruleId && (
        <div className="text-[11px] text-amber-800 mt-2"
             data-testid={`roofing-legacy-no-rule-${system.system_type}`}>
          No approved PricingRule yet — approve pricing first, then classify the assembly.
        </div>
      )}
      {ruleId && options && options.length > 0 && (
        <div className="mt-2 space-y-1">
          {options.map((opt) => (
            <label key={opt.key}
                   className="flex items-start gap-2 text-xs p-2 rounded border border-[var(--jc-border)] hover:bg-[var(--jc-surface)]/50 cursor-pointer"
                   data-testid={`roofing-legacy-option-${opt.key}`}>
              <input
                type="radio"
                name={`legacy-${system.system_type}`}
                checked={choice === opt.key}
                onChange={() => setChoice(opt.key)}
                className="mt-0.5"
                data-testid={`roofing-legacy-radio-${opt.key}`}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={classify}
              disabled={!choice || saving}
              className="text-xs px-3 py-1 rounded jc-btn-primary disabled:opacity-50"
              data-testid={`roofing-legacy-confirm-${system.system_type}`}
            >
              {saving ? "Saving…" : "Confirm classification"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Slice 2B1 · Custom / One-Off Roofing systems
// ============================================================================
const CustomSystemsSection = ({ roofingData, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [usage, setUsage] = useState("reusable");
  const [saving, setSaving] = useState(false);
  const customs = (roofingData?.systems || []).filter(s => (s.meta || {}).custom_slug);

  const create = async () => {
    if (!name.trim()) { toast.error("Please enter a name"); return; }
    setSaving(true);
    try {
      await api.post(`/onboarding/trade-setup/trade/Roofing/system/custom`,
        { display_name: name.trim(), custom_usage: usage });
      toast.success("Custom system added");
      setName(""); setOpen(false);
      onSaved && onSaved();
    } catch { toast.error("Failed to create custom system"); }
    finally { setSaving(false); }
  };

  return (
    <div className="jc-card p-3 border-l-4 border-l-[var(--jc-border-strong)]"
         data-testid="custom-systems-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Custom / One-Off Roofing</div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            Add a Roofing system that isn't in the pilot list — either a reusable one you offer often, or a one-off scope for a single job.
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)}
                className="text-xs px-2 py-1 rounded jc-btn-primary"
                data-testid="custom-systems-toggle">
          {open ? "Close" : "Add custom system"}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-2 text-sm" data-testid="custom-systems-form">
          <div>
            <label className="text-xs font-semibold">Display name</label>
            <input value={name}
                   onChange={e => setName(e.target.value)}
                   placeholder="e.g. Modified Bitumen — Torched"
                   data-testid="custom-systems-name"
                   className="w-full mt-1 border rounded p-2" />
          </div>
          <div>
            <label className="text-xs font-semibold">Usage</label>
            <select value={usage}
                    onChange={e => setUsage(e.target.value)}
                    data-testid="custom-systems-usage"
                    className="w-full mt-1 border rounded p-2">
              <option value="reusable">Reusable — I offer this often</option>
              <option value="one_off">One-off — this job only</option>
            </select>
          </div>
          <button onClick={create} disabled={saving}
                  className="text-xs px-3 py-2 rounded jc-btn-primary"
                  data-testid="custom-systems-create">
            Create
          </button>
        </div>
      )}
      {customs.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="custom-systems-list">
          {customs.map(c => (
            <li key={c.meta.custom_slug}
                className="text-xs rounded bg-[var(--jc-bg)] px-2 py-1"
                data-testid={`custom-systems-item-${c.meta.custom_slug}`}>
              <div className="font-medium">{c.meta.custom_display_name}</div>
              <div className="text-[10px] text-[var(--jc-text-soft)]">
                {c.meta.custom_usage === "one_off" ? "One-off — this job only" : "Reusable"} · Readiness: {c.readiness?.kind || "not_started"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ============================================================================
// Slice 2B1 · Historical (legacy) pricing rows — read-only review
// ============================================================================
const HistoricalPricingRows = ({ roofingData, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [assocMap, setAssocMap] = useState({});
  const [expanded, setExpanded] = useState(false);
  const systemChoices = (roofingData?.systems || []).map(s => s.system_type);

  const load = async () => {
    try {
      const [{ data: profile }, { data: assocs }] = await Promise.all([
        api.get("/pricing-profile"),
        api.get("/onboarding/trade-setup/historical-row-associations"),
      ]);
      const all = profile.material_rules || [];
      const legacy = all.filter(r => !((r.starter_key || "").startsWith("slice2b1.")));
      setRows(legacy);
      const map = {};
      for (const a of (assocs.associations || [])) {
        map[a.pricing_rule_id] = a.system_type;
      }
      setAssocMap(map);
    } catch { setRows([]); setAssocMap({}); }
  };
  useEffect(() => { load(); }, []);

  const associate = async (rule_id, associated_system_type) => {
    try {
      await api.post(`/onboarding/trade-setup/pricing-rule/${rule_id}/associate`,
        { associated_system_type });
      toast.success(associated_system_type
        ? `Associated with ${associated_system_type}`
        : "Association cleared");
      await load();
    } catch { toast.error("Update failed"); }
  };

  if (!rows.length) return null;
  return (
    <div className="jc-card p-3 border-l-4 border-l-amber-400"
         data-testid="historical-pricing-rows">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Existing Pricing Rows</div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            {rows.length} pricing row{rows.length === 1 ? "" : "s"} from earlier setup. Read-only review — associations are stored separately as pure tenant metadata and never touch the pricing row itself. Historical rows can't make a system Ready on their own.
          </div>
        </div>
        <button onClick={() => setExpanded(x => !x)}
                className="text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)]"
                data-testid="historical-pricing-rows-toggle">
          {expanded ? "Hide" : "Review"}
        </button>
      </div>
      {expanded && (
        <ul className="mt-3 space-y-1 text-xs" data-testid="historical-pricing-rows-list">
          {rows.slice(0, 100).map(r => {
            const assoc = assocMap[r.id] || "";
            return (
              <li key={r.id}
                  className="rounded bg-[var(--jc-bg)] px-2 py-1 flex flex-wrap items-center justify-between gap-2"
                  data-testid={`historical-row-${r.id}`}>
                <div className="truncate min-w-0 flex-1">
                  <span className="font-medium">{r.name || "(unnamed)"}</span>
                  <span className="ml-2 text-[10px] text-[var(--jc-text-soft)]">
                    {r.trade || "?"} · {r.system_type || "?"} · {r.pricing_type || "unit_priced"}
                  </span>
                  {assoc && (
                    <span className="ml-2 text-[10px] rounded bg-emerald-100 text-emerald-800 px-1"
                          data-testid={`historical-row-${r.id}-assoc-badge`}>
                      associated with {assoc}
                    </span>
                  )}
                </div>
                <select value={assoc}
                        onChange={e => associate(r.id, e.target.value)}
                        className="text-[11px] border rounded p-1"
                        data-testid={`historical-row-${r.id}-associate`}>
                  <option value="">— No system association —</option>
                  {systemChoices.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// helpers used by workbook + refdoc panels — they call fetch directly with
// the same axios-configured base URL and auth headers to avoid multipart
// serialization edge cases in the shared axios instance.
function axiosBase() {
  const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return url;
}
function authHeader() {
  const t = localStorage.getItem("jc_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}
